import Joi from 'joi'
import clsx from 'clsx'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'

import SettingHint from '../SettingHint'

import { REQUIREMENT_IDS, getRequirementCatalog } from '../../../../../../shared/activationRequirements'

import styles from '../style.module.scss'
import config from './style.module.scss'

/*
 * Die Stellschrauben zur Vorschau darüber.
 *
 * Bis 05.08.2026 war hier nur der Kursnachweis einstellbar; die übrigen sieben
 * Punkte standen ohne jede Konfiguration in der Liste, weil das Backend sie
 * fest prüft. Für die Administration hieß das: eine Checkliste, an der sich
 * nichts ändern lässt, und daneben eine einzelne Karte, deren Bezug zur Liste
 * man erraten musste.
 *
 * Jetzt trägt jeder Punkt der Checkliste seine Zeile mit einem Schalter
 * „aktiviert/deaktiviert". Kommen bei einem Punkt weitere Parameter dazu, ist
 * die Zeile der Ort dafür.
 *
 * Bis 31.10.2026 stand hier auch der Kursnachweis mit Bezeichnung, Kurs-Link
 * und Unternehmensformen (Einstellung `courseCertificateRequirement`). Er ist
 * ausgebaut, seit die Kurse in der Anwendung selbst laufen.
 *
 * Abgeschaltet heißt: Der Punkt fällt aus der Checkliste der Schüler:innen und
 * damit aus der Bedingung zum Einreichen. Was das Backend im Hintergrund
 * weiterhin erhebt, bleibt unberührt — es entscheidet nicht über das Einreichen.
 */

const schema = Joi.object({
	activationRequirements: Joi.object().pattern(Joi.string(), Joi.boolean())
})

export default function RequirementsConfig(props) {
	const { t } = useTranslation()
	const { register, reset, watch, handleSubmit } = useForm({ resolver: joiResolver(schema) })

	const rows = getRequirementCatalog(props.form)

	const onSubmit = async form => {
		const switches = REQUIREMENT_IDS.reduce((acc, id) => {
			acc[id] = !!form.activationRequirements?.[id]
			return acc
		}, {})

		await props.updateOptions({ name: 'activationRequirements', value: switches })

		toast.success(t('Options was updated'))
	}

	useEffect(() => {
		const stored = props.form?.activationRequirements

		reset({
			activationRequirements: REQUIREMENT_IDS.reduce((acc, id) => {
				// Ohne gespeicherten Wert eingeschaltet — ein fehlender Eintrag darf
				// keine Voraussetzung stillschweigend fallen lassen.
				acc[id] = stored?.[id] !== false
				return acc
			}, {})
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="activationRequirementsConfig" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>{t('Configure requirements')}</h3>
				<SettingHint
					example={t(
						'Example: if no experts are needed in a given year, switch off that item — it then disappears from the checklist.'
					)}
					warning={t(
						'A switched-off requirement is no longer checked before submission. It disappears from the checklist above at once — including for companies that are currently filling it in.'
					)}
				>
					{t(
						'Every item of the checklist above with its setting. Switched off, it disappears from the students’ checklist and is no longer required for submission.'
					)}
				</SettingHint>
				<div className={styles.sectionContent}>
					<ol className={config.list}>
						{rows.map((row, index) => {
							const on = watch(`activationRequirements.${row.id}`)

							return (
								<li key={row.id} className={clsx(config.item, !on && config.itemOff)}>
									<div className={config.head}>
										<span className={config.number}>{index + 1}</span>
										<div className={config.headText}>
											<p className={config.title}>{row.title}</p>
											<p className={config.text}>{row.text}</p>
										</div>
										<label className={config.switch}>
											<input type="checkbox" {...register(`activationRequirements.${row.id}`)} />
											<span className={config.track} aria-hidden="true" />
											<span className={config.state}>
												{on ? t('activated') : t('deactivated')}
											</span>
										</label>
									</div>
								</li>
							)
						})}
					</ol>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

RequirementsConfig.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
